import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { firestore, auth } from "../firebase";
import useAuth from "../hooks/useAuth";
import { priceQuote } from "../utils/pricing";
import { formatINR } from "../utils/format";
import { generateQuoteNumber } from "../utils/numbering";
import { generateQuotePdfClient } from "../utils/pdf";
import { downloadPdf, openOrDownloadPdf } from "../utils/download";
import TopBar from "./TopBar";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { FiCheck, FiCheckCircle, FiFileText } from "react-icons/fi";
import "../styles/quotation-system.css";

export default function QuoteForm() {
  const location = useLocation();
  React.useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "robots";
    metaTag.content = "noindex";
    document.head.appendChild(metaTag);
    document.body.classList.add('quotation-page');
    return () => {
      document.head.removeChild(metaTag);
      document.body.classList.remove('quotation-page');
    };
  }, []);

  const { user, profile } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [savedQuote, setSavedQuote] = useState(null);
  const { modal, showAlert, hideModal } = useModal();

  // Pre-fill customer data from location state (when coming from lead)
  const prefillData = location.state?.leadData || null;
  const leadId = location.state?.leadId || null;
  const existingQuoteId = location.state?.quoteId || null; // For re-generation

  const [customer, setCustomer] = useState(() => {
    if (prefillData) {
      return {
        name: prefillData.name || "",
        phone: prefillData.phone !== 'Not available' ? prefillData.phone : "",
        address: prefillData.address || ""
      };
    }
    return { name: "", phone: "", address: "" };
  });
  const [inputs, setInputs] = useState({
    roof: "rcc",
    sanctionedLoadKw: "",
    avgMonthlyUnits: "",
    phase: "single",
    connection: "residential",
    wantsSubsidy: false,
    requestedCapacityKw: "",
    panelOverride: "",
    structureType: "elevated", // 'elevated' | 'portrait' (only for RCC roof)
    customBasePrice: "", // Admin-only: Custom base price override
  });

  const derived = useMemo(() => {
    try {
      const d = priceQuote({
        roof: inputs.roof,
        phase: inputs.phase,
        connection: inputs.connection,
        wantsSubsidy: !!inputs.wantsSubsidy,
        requestedCapacityKw: Number(inputs.requestedCapacityKw) || undefined,
        sanctionedLoadKw: Number(inputs.sanctionedLoadKw) || undefined,
        avgMonthlyUnits: Number(inputs.avgMonthlyUnits) || 0,
        panelOverride: inputs.panelOverride || undefined,
        structureType: inputs.roof === "rcc" ? inputs.structureType : undefined,
        customBasePrice: inputs.customBasePrice ? Number(inputs.customBasePrice) : undefined,
      });
      return d;
    } catch (e) {
      return null;
    }
  }, [inputs]);

  // Uncheck subsidy checkbox if it becomes disabled (commercial or capacity > 5kW or capacity > 10kW)
  useEffect(() => {
    if (inputs.wantsSubsidy && (inputs.connection === "commercial" || (derived && derived.capacityKw > 5) || (derived && derived.capacityKw > 10))) {
      setInputs(prev => ({ ...prev, wantsSubsidy: false }));
    }
  }, [inputs.connection, derived, inputs.wantsSubsidy]);

  const subsidyDisabled =
    inputs.connection === "commercial" || (derived && derived.capacityKw > 5) || (derived && derived.capacityKw > 10);

  const onGenerate = async () => {
    if (!user || !profile) return;
    if (!customer.name || !customer.phone) {
      showAlert("Please enter customer name and phone.", "warning", "Required Fields");
      return;
    }
    if (!derived) {
      showAlert("Please complete pricing inputs.", "warning", "Incomplete Form");
      return;
    }
    
    // Validation for systems above 10kW
    if (derived.capacityKw > 10) {
      const roundedCapacity = Math.round(derived.capacityKw);
      const isMultipleOf5 = roundedCapacity % 5 === 0;
      
      // Check if capacity is a multiple of 5kW
      if (!isMultipleOf5) {
        showAlert(
          `For systems above 10kW, capacity must be in multiples of 5kW. Your requirement of ${roundedCapacity}kW is not available. Please contact the admin for a custom quotation.`,
          "info",
          "Capacity Not Available"
        );
        return;
      }
      
      // Check if capacity exists in pricing table (11, 12, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100)
      const validCapacities = [11, 12, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100];
      if (!validCapacities.includes(roundedCapacity)) {
        showAlert(
          `Pricing is not available for ${roundedCapacity}kW systems. Please contact the admin for a custom quotation.`,
          "info",
          "Pricing Not Available"
        );
        return;
      }
      
      // Force commercial connection for systems above 10kW
      if (inputs.connection !== "commercial") {
        setInputs(prev => ({ ...prev, connection: "commercial" }));
        // Connection type will be updated, continue with quote generation
      }
    }
    setSubmitting(true);
    try {
      const quoteNo = await generateQuoteNumber({ userId: user.uid, codePrefix: profile.codePrefix || "XX" });
      const createdAt = Date.now();
      const docRef = firestore.collection("quotes").doc();

      const inputPayload = {
        roof: inputs.roof,
        sanctionedLoadKw: Number(inputs.sanctionedLoadKw) || 0,
        avgMonthlyUnits: Number(inputs.avgMonthlyUnits) || 0,
        phase: inputs.phase,
        connection: inputs.connection,
        wantsSubsidy: !!inputs.wantsSubsidy,
      };
      // Only include structureType if roof is RCC (Firebase doesn't accept undefined)
      if (inputs.roof === "rcc" && inputs.structureType) {
        inputPayload.structureType = inputs.structureType;
      }
      const requestedCap = Number(inputs.requestedCapacityKw);
      if (!Number.isNaN(requestedCap) && requestedCap > 0) {
        inputPayload.requestedCapacityKw = requestedCap;
      }
      if (inputs.panelOverride) {
        inputPayload.panelOverride = inputs.panelOverride;
      }
      // Only include customBasePrice if user is admin and value is provided
      if (profile?.role === "admin" && inputs.customBasePrice) {
        inputPayload.customBasePrice = Number(inputs.customBasePrice);
      }

      // If regenerating, use existing quote ID, otherwise create new
      const quoteDocId = existingQuoteId || docRef.id;
      const quoteDocRef = existingQuoteId 
        ? firestore.collection("quotes").doc(existingQuoteId)
        : docRef;

      // Get existing quote data if regenerating
      let existingQuote = null;
      if (existingQuoteId) {
        const existingDoc = await quoteDocRef.get();
        existingQuote = existingDoc.data();
      }

      const quote = {
        meta: {
          id: quoteDocId,
          quoteNo: existingQuote?.meta?.quoteNo || quoteNo,
          salespersonUid: user.uid,
          salespersonName: profile?.name || auth.currentUser?.email || "Sales",
          createdAt: existingQuote?.meta?.createdAt || createdAt,
          updatedAt: Date.now(),
        },
        customer,
        inputs: inputPayload,
        derived,
      };
      await quoteDocRef.set(quote, { merge: false }); // Overwrite completely for regeneration
      const { blob, url, filename } = await generateQuotePdfClient({ quote });
      
      // If this quote is linked to a lead, update the lead's quoteId and quote generation timestamp
      if (leadId) {
        try {
          const updateData = {
            quoteId: quoteDocId,
            quoteGeneratedAt: Date.now()
          };
          await firestore.collection("shortlistedLeads").doc(leadId).update(updateData);
        } catch (error) {
          console.error("Error linking quote to lead:", error);
        }
      }
      
      // Store quote data and URL for download/regeneration
      setSavedQuote(quote);
      setDownloadUrl(url);
      
      // Auto-open in new tab on desktop, download on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        // On mobile, trigger download immediately
        downloadPdf(blob, filename);
      } else {
        // On desktop, open in new tab
        openOrDownloadPdf(blob, filename, false);
      }
      
      setStep(3);
    } catch (e) {
      // Log detailed error for debugging and show friendly message.
      // eslint-disable-next-line no-console
      console.error("Failed to generate quote", e);
      showAlert("Failed to generate quote. Please check network and Firebase config.", "error", "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="quotation-system">
      <TopBar />
      <Modal {...modal} onClose={hideModal} />
      <div className="elegant-container fade-in-up">
        <div className="elegant-card">
          <h1 className="elegant-title">Quotation Generator</h1>
          <p className="elegant-subtitle">Create professional quotations in minutes</p>
          
          {/* Step Indicator */}
          <div className="step-indicator">
            <div className={`step-item ${step >= 1 ? "active" : ""} ${step > 1 ? "completed" : ""}`}>
              <div className="step-circle">
                {step > 1 ? <FiCheck style={{ fontSize: "1.25rem" }} /> : "1"}
              </div>
              <span className="step-label">Customer</span>
            </div>
            <div className={`step-item ${step >= 2 ? "active" : ""} ${step > 2 ? "completed" : ""}`}>
              <div className="step-circle">
                {step > 2 ? <FiCheck style={{ fontSize: "1.25rem" }} /> : "2"}
              </div>
              <span className="step-label">Pricing</span>
            </div>
            <div className={`step-item ${step >= 3 ? "active" : ""}`}>
              <div className="step-circle">3</div>
              <span className="step-label">Complete</span>
            </div>
          </div>

          {step === 1 && (
            <div className="fade-in-up">
              <h2 className="elegant-section-title">Customer Details</h2>
              <p style={{ color: "var(--neutral-600)", marginBottom: "2rem", fontSize: "0.9375rem" }}>
                This information will be printed on the quotation
              </p>
              
              <div className="form-group-elegant">
                <label className="form-label-elegant">Customer Name *</label>
                <input 
                  className="form-input-elegant"
                  value={customer.name} 
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })} 
                  placeholder="Enter customer name"
                  required
                />
              </div>
              
              <div className="form-group-elegant">
                <label className="form-label-elegant">Phone Number *</label>
                <input 
                  className="form-input-elegant"
                  value={customer.phone} 
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} 
                  placeholder="+91 9876543210"
                  required
                />
              </div>
              
              <div className="form-group-elegant">
                <label className="form-label-elegant">Address (Optional)</label>
                <textarea 
                  className="form-input-elegant"
                  value={customer.address} 
                  onChange={(e) => setCustomer({ ...customer, address: e.target.value })} 
                  placeholder="Enter customer address"
                  rows={3}
                  style={{ resize: "vertical", fontFamily: "inherit", position: "relative", zIndex: 1 }}
                />
              </div>
              
              <button 
                onClick={() => setStep(2)} 
                className="btn-elegant btn-elegant-primary"
                style={{ width: "100%", marginTop: "1rem" }}
                disabled={!customer.name || !customer.phone}
              >
                Continue to Pricing →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="fade-in-up">
              <h2 className="elegant-section-title">Pricing Configuration</h2>
              <p style={{ color: "var(--neutral-600)", marginBottom: "2rem", fontSize: "0.9375rem" }}>
                These details are used for calculation only and won't appear on the quotation
              </p>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1.5rem", marginBottom: "2rem" }} className="pricing-grid">
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Roof Type</label>
                  <select 
                    className="form-select-elegant"
                    value={inputs.roof} 
                    onChange={(e) => {
                      const newRoof = e.target.value;
                      setInputs({ 
                        ...inputs, 
                        roof: newRoof,
                        // Reset structure type if switching away from RCC
                        structureType: newRoof === "rcc" ? (inputs.structureType || "elevated") : undefined
                      });
                    }}
                  >
                    <option value="rcc">RCC</option>
                    <option value="sheet">Sheet</option>
                  </select>
                </div>
                
                {inputs.roof === "rcc" && (
                  <div className="form-group-elegant">
                    <label className="form-label-elegant">Structure Type</label>
                    <div style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="structureType"
                          value="elevated"
                          checked={inputs.structureType === "elevated"}
                          onChange={(e) => setInputs({ ...inputs, structureType: e.target.value })}
                          style={{ cursor: "pointer" }}
                        />
                        <span>Elevated</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="structureType"
                          value="portrait"
                          checked={inputs.structureType === "portrait"}
                          onChange={(e) => setInputs({ ...inputs, structureType: e.target.value })}
                          style={{ cursor: "pointer" }}
                        />
                        <span>Portrait</span>
                      </label>
                    </div>
                  </div>
                )}
                
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Sanctioned Load (kW)</label>
                  <input 
                    type="number"
                    inputMode="numeric"
                    className="form-input-elegant"
                    value={inputs.sanctionedLoadKw} 
                    onChange={(e) => setInputs({ ...inputs, sanctionedLoadKw: e.target.value })} 
                    placeholder="e.g., 5"
                  />
                </div>
                
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Avg Units / Month</label>
                  <input 
                    type="number"
                    inputMode="numeric"
                    className="form-input-elegant"
                    value={inputs.avgMonthlyUnits} 
                    onChange={(e) => setInputs({ ...inputs, avgMonthlyUnits: e.target.value })} 
                    placeholder="e.g., 600"
                  />
                </div>
                
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Phase</label>
                  <select 
                    className="form-select-elegant"
                    value={inputs.phase} 
                    onChange={(e) => setInputs({ ...inputs, phase: e.target.value })}
                  >
                    <option value="single">Single Phase</option>
                    <option value="three">Three Phase</option>
                  </select>
                </div>
                
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Connection Type</label>
                  <select 
                    className="form-select-elegant"
                    value={inputs.connection} 
                    onChange={(e) => setInputs({ ...inputs, connection: e.target.value })}
                    disabled={derived && derived.capacityKw > 10}
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                  {derived && derived.capacityKw > 10 && (
                    <p style={{ fontSize: "0.75rem", color: "var(--accent-purple)", marginTop: "0.25rem", marginLeft: "0.5rem" }}>
                      Systems above 10kW are commercial only
                    </p>
                  )}
                </div>
                
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Specific Capacity (kW, Optional)</label>
                  <input 
                    type="number"
                    inputMode="numeric"
                    className="form-input-elegant"
                    value={inputs.requestedCapacityKw} 
                    onChange={(e) => setInputs({ ...inputs, requestedCapacityKw: e.target.value })} 
                    placeholder="Override calculated capacity"
                  />
                  {derived && derived.capacityKw > 10 && (
                    <p style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginTop: "0.25rem", marginLeft: "0.5rem" }}>
                      For systems above 10kW, available capacities: 11, 12, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100 kW
                    </p>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <div className="checkbox-elegant" style={{ marginBottom: "1rem" }}>
                  <label className="checkbox-box" htmlFor="subsidy">
                    <input 
                      type="checkbox" 
                      checked={inputs.wantsSubsidy} 
                      onChange={(e) => setInputs({ ...inputs, wantsSubsidy: e.target.checked })} 
                      disabled={subsidyDisabled}
                      id="subsidy"
                    />
                    <span className="checkmark"></span>
                  </label>
                  <span
                    className="checkbox-text"
                    style={{
                      fontWeight: "500",
                      opacity: subsidyDisabled ? 0.6 : 1,
                      cursor: "default"
                    }}
                  >
                    Customer wants subsidy (Residential only, up to 5kW)
                  </span>
                </div>
                {subsidyDisabled && (
                  <p style={{ 
                    fontSize: "0.875rem", 
                    color: "var(--neutral-500)", 
                    marginTop: "0.5rem",
                    marginLeft: "0.5rem"
                  }}>
                    {inputs.connection === "commercial" 
                      ? "Subsidy is only available for residential systems." 
                      : "Subsidy is only available for systems up to 5kW."}
                  </p>
                )}
                
                <div className="form-group-elegant">
                  <label className="form-label-elegant">Override Panel Type</label>
                  <select 
                    className="form-select-elegant"
                    value={inputs.panelOverride} 
                    onChange={(e) => setInputs({ ...inputs, panelOverride: e.target.value })}
                  >
                    <option value="">Auto (Based on subsidy preference)</option>
                    <option value="NON-DCR">Force NON-DCR</option>
                  </select>
                </div>
                
                {profile?.role === "admin" && (
                  <div className="form-group-elegant" style={{ gridColumn: "1 / -1", border: "2px solid var(--accent-purple)", padding: "1rem", borderRadius: "8px", background: "rgba(139, 92, 246, 0.05)" }}>
                    <label className="form-label-elegant" style={{ color: "var(--accent-purple)", fontWeight: "600" }}>
                      Custom Base Price (Admin Only) <span style={{ fontSize: "0.75rem", fontWeight: "400", color: "var(--neutral-600)" }}>- Overrides standard pricing</span>
                    </label>
                    <input 
                      type="number"
                      inputMode="numeric"
                      className="form-input-elegant"
                      value={inputs.customBasePrice} 
                      onChange={(e) => setInputs({ ...inputs, customBasePrice: e.target.value })} 
                      placeholder="Enter custom base price (pre-GST)"
                      style={{ borderColor: inputs.customBasePrice ? "var(--accent-purple)" : undefined }}
                    />
                    {inputs.customBasePrice && derived && derived.originalBasePrice && (
                      <p style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginTop: "0.5rem" }}>
                        Standard price: {formatINR(derived.originalBasePrice)} | Custom price: {formatINR(Number(inputs.customBasePrice))}
                      </p>
                    )}
                    {inputs.customBasePrice && (
                      <button
                        type="button"
                        onClick={() => setInputs({ ...inputs, customBasePrice: "" })}
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.5rem 1rem",
                          background: "var(--neutral-200)",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          color: "var(--neutral-700)"
                        }}
                      >
                        Clear Custom Price
                      </button>
                    )}
                  </div>
                )}
              </div>

              {derived && (
                <div style={{ 
                  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(236, 72, 153, 0.08) 50%, rgba(6, 182, 212, 0.08) 100%)", 
                  padding: "1.5rem", 
                  marginTop: "1.5rem", 
                  borderRadius: "16px",
                  border: "2px solid transparent",
                  backgroundImage: "linear-gradient(white, white), linear-gradient(135deg, #8b5cf6, #ec4899, #06b6d4)",
                  backgroundOrigin: "border-box",
                  backgroundClip: "padding-box, border-box",
                  boxShadow: "0 4px 20px rgba(139, 92, 246, 0.15)"
                }}>
                  <h3 style={{ marginBottom: "1rem", color: "var(--primary-deep)", fontSize: "1.125rem", fontWeight: "600" }}>
                    Pricing Summary
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }} className="pricing-summary-grid">
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>Capacity</div>
                      <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "var(--primary-deep)" }}>
                        {derived.capacityKw} kW
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "var(--neutral-500)" }}>{derived.panelType}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>Solar System (without GST)</div>
                      <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "var(--primary-deep)" }}>
                        {formatINR(derived.basePrice)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>Installation, transportation, labor & DISCOM fees (without GST)</div>
                      <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "var(--primary-deep)" }}>
                        {formatINR(derived.logisticsBase || derived.logisticsInclusive)}
                      </div>
                    </div>
                    {(() => {
                      // Calculate GST values if not present (for backward compatibility)
                      const logisticsBase = derived.logisticsBase || derived.logisticsInclusive || 0;
                      let gst5Percent = derived.gst5Percent;
                      let gst18Percent = derived.gst18Percent;
                      if (gst5Percent === undefined || gst18Percent === undefined) {
                        // Fallback: calculate from total system price (basePrice + logisticsBase)
                        const systemTotal = (derived.basePrice || 0) + logisticsBase;
                        const systemTotal70Percent = systemTotal * 0.7;
                        const systemTotal30Percent = systemTotal * 0.3;
                        gst5Percent = Math.round((5 / 100) * systemTotal70Percent);
                        gst18Percent = Math.round((18 / 100) * systemTotal30Percent);
                      }
                      const basePrice = derived.basePrice || 0;
                      const total = basePrice + logisticsBase + gst5Percent + gst18Percent;
                      return (
                        <>
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>GST 5% on 70% of system price</div>
                            <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "var(--primary-deep)" }}>
                              {formatINR(gst5Percent)}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>GST 18% on 30% of system price</div>
                            <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "var(--primary-deep)" }}>
                              {formatINR(gst18Percent)}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>Total</div>
                            <div style={{ 
                              fontSize: "1.5rem", 
                              fontWeight: "700", 
                              color: "var(--accent-purple)"
                            }}>
                              {formatINR(total)}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                    {derived.subsidyApplicable && (
                      <div>
                        <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>Subsidy (Info)</div>
                        <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "var(--success)" }}>
                          {formatINR(derived.subsidyAmount)}
                        </div>
                      </div>
                    )}
                    {inputs.wantsSubsidy && derived.subsidyApplicable && derived.subsidyAmount > 0 && (() => {
                      // Calculate GST values if not present (for backward compatibility)
                      const logisticsBase = derived.logisticsBase || derived.logisticsInclusive || 0;
                      let gst5Percent = derived.gst5Percent;
                      let gst18Percent = derived.gst18Percent;
                      if (gst5Percent === undefined || gst18Percent === undefined) {
                        // Fallback: calculate from total system price (basePrice + logisticsBase)
                        const systemTotal = (derived.basePrice || 0) + logisticsBase;
                        const systemTotal70Percent = systemTotal * 0.7;
                        const systemTotal30Percent = systemTotal * 0.3;
                        gst5Percent = Math.round((5 / 100) * systemTotal70Percent);
                        gst18Percent = Math.round((18 / 100) * systemTotal30Percent);
                      }
                      const basePrice = derived.basePrice || 0;
                      const total = basePrice + logisticsBase + gst5Percent + gst18Percent;
                      const finalWithSubsidy = Math.max(0, total - (derived.subsidyAmount || 0));
                      return (
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--neutral-600)", marginBottom: "0.25rem" }}>Final Price (with subsidy)</div>
                          <div style={{ 
                            fontSize: "1.5rem", 
                            fontWeight: "700", 
                            color: "var(--success)"
                          }}>
                            {formatINR(finalWithSubsidy)}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }} className="form-actions-mobile">
                <button 
                  onClick={() => setStep(1)} 
                  className="btn-elegant btn-elegant-secondary"
                  style={{ flex: 1 }}
                >
                  ← Back
                </button>
                <button 
                  onClick={onGenerate} 
                  disabled={submitting || !derived}
                  className="btn-elegant btn-elegant-accent"
                  style={{ flex: 2 }}
                >
                  {submitting ? (
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                      <span className="loading-elegant"></span>
                      Generating PDF...
                    </span>
                  ) : (
                    "Generate PDF →"
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fade-in-up" style={{ textAlign: "center", padding: "3rem 2rem" }}>
              <div style={{ 
                width: "80px", 
                height: "80px", 
                margin: "0 auto 2rem", 
                background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #06b6d4 100%)", 
                borderRadius: "50%", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(139, 92, 246, 0.4), 0 4px 12px rgba(236, 72, 153, 0.3)",
                position: "relative"
              }}>
                <FiCheckCircle style={{ fontSize: "3rem", zIndex: 1, position: "relative", color: "white" }} />
                <div style={{
                  position: "absolute",
                  inset: "-4px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #06b6d4 100%)",
                  opacity: 0.3,
                  filter: "blur(8px)",
                  zIndex: 0
                }}></div>
              </div>
              <h2 className="elegant-title" style={{ fontSize: "2rem", marginBottom: "1rem", color: "var(--primary-deep)" }}>Quotation Generated!</h2>
              <p className="elegant-subtitle" style={{ marginBottom: "1rem" }}>
                Your quotation has been created and saved successfully
              </p>
              {/iPhone|iPad|iPod/i.test(navigator.userAgent) && (
                <div style={{ 
                  fontSize: "0.875rem", 
                  color: "var(--accent-purple)", 
                  fontWeight: "600",
                  marginBottom: "2rem",
                  padding: "1rem",
                  background: "rgba(139, 92, 246, 0.1)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <p style={{ margin: "0 0 0.5rem 0" }}>📱 PDF opened in new tab</p>
                  <p style={{ fontSize: "0.8125rem", fontWeight: "400", color: "var(--neutral-600)", margin: 0 }}>
                    Tap the Share button → "Save to Files" to save
                  </p>
                </div>
              )}
              {/Android/i.test(navigator.userAgent) && (
                <p style={{ 
                  fontSize: "0.875rem", 
                  color: "var(--accent-purple)", 
                  fontWeight: "600",
                  marginBottom: "2rem",
                  padding: "0.75rem",
                  background: "rgba(139, 92, 246, 0.1)",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  ✓ PDF downloaded to your device
                </p>
              )}
              <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }} className="form-actions-mobile">
                <button
                  onClick={async () => {
                    if (!savedQuote) {
                      // Fallback to opening URL if quote data not available
                      window.open(downloadUrl, "_blank");
                      return;
                    }
                    try {
                      const { blob, filename } = await generateQuotePdfClient({ quote: savedQuote });
                      // On desktop: open in new tab, on mobile: download
                      openOrDownloadPdf(blob, filename);
                    } catch (error) {
                      console.error("Failed to regenerate PDF:", error);
                      // Fallback to opening URL if regenerating fails
                      window.open(downloadUrl, "_blank");
                    }
                  }}
                  className="btn-elegant btn-elegant-accent"
                  style={{ minWidth: "140px", minHeight: "48px" }}
                >
                  {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? "Download PDF" : "Open PDF"}
                </button>
                <button 
                  onClick={() => {
                    setStep(1);
                    setCustomer({ name: "", phone: "", address: "" });
                    setInputs({
                      roof: "rcc",
                      sanctionedLoadKw: "",
                      avgMonthlyUnits: "",
                      phase: "single",
                      connection: "residential",
                      wantsSubsidy: false,
                      requestedCapacityKw: "",
                      panelOverride: "",
                      structureType: "elevated",
                      customBasePrice: "",
                    });
                    setDownloadUrl("");
                    setSavedQuote(null);
                  }}
                  className="btn-elegant btn-elegant-secondary"
                  style={{ minWidth: "140px", minHeight: "48px" }}
                >
                  Create Another
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


